import { ReactNode } from 'react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import Text from 'components/_core/Text';
import { logEvent } from 'features/Analytics/analytics';
import { useIsBrowsingDisabled } from 'layouts/AppLayout/context';

import { useIsLinkActive } from '../hooks/useIsLinkActive';
import {
  AnimatedLinkContainer,
  AnimatedLinkContainerProps,
} from './AnimatedLinkContainer';
import { NavigationHighlight } from './NavigationHighlight';

export type NavigationEntryProps = Omit<
  AnimatedLinkContainerProps,
  'isActive' | 'disabled'
> & {
  children: ReactNode;
  /**
   * Controls the visibility of the highlight for the given entry. It's usually used to indicate that the entry is new for the user.
   */
  isHighlighted?: boolean;
};

export function NavigationEntry({
  children,
  isHighlighted = false,
  onClick,
  to,
  ...props
}: NavigationEntryProps) {
  const isActive = useIsLinkActive(to);
  const isBrowsingDisabled = useIsBrowsingDisabled();
  const { companyProfileId, companyUserId } = useCompanyContext();

  return (
    <AnimatedLinkContainer
      disabled={isBrowsingDisabled}
      isActive={isActive}
      onClick={(e) => {
        logEvent({
          name: 'Click_Sub_Menu_Link',
          properties: {
            companyProfileId,
            companyUserId,
            to,
          },
        });
        onClick?.(e);
      }}
      paddingLeft="44px"
      paddingY="space-8"
      to={to}
      {...props}
    >
      <Text
        alignItems="center"
        display="flex"
        variant={isActive ? 'primary' : 'secondary'}
      >
        {children}
      </Text>
      {isHighlighted ? <NavigationHighlight /> : null}
    </AnimatedLinkContainer>
  );
}
